import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { omitReservedManagedAuthPrivateData } from '../config/r2rManagedAuthPrivateData';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import {
  WATCHED_LISTING_IDS_KEY,
  WATCH_LIMIT_REACHED,
  computeNextWatchedListings,
  createWatchedListingSnapshot,
  getWatchedListingEntries,
  toListingIdString,
} from '../util/watchedListings';

import { setCurrentUser } from './user.duck';

// ================ Async Thunks ================ //

/**
 * Toggle a listing in currentUser.profile.privateData.watchedListingIds.
 * Stores snapshot objects ({ id, title, imageUrl, price, watchedAt }).
 * Newest watched entry is prepended. Caps at MAX_WATCHED_LISTINGS.
 *
 * @param {Object} listing - Listing entity (full listing preferred so snapshot can be saved)
 */
export const toggleWatchedListingThunk = createAsyncThunk(
  'watchedListings/toggleWatchedListing',
  (listing, { getState, extra: sdk, dispatch, rejectWithValue }) => {
    const listingIdStr = toListingIdString(listing?.id);
    if (!listingIdStr) {
      return rejectWithValue(storableError(new Error('Missing listing id')));
    }

    const currentUser = getState().user.currentUser;
    if (!currentUser) {
      return rejectWithValue(storableError(new Error('Not authenticated')));
    }

    const currentEntries = getWatchedListingEntries(currentUser);
    const snapshot = createWatchedListingSnapshot(listing);
    const result = computeNextWatchedListings(currentEntries, listingIdStr, snapshot);
    if (result.error === WATCH_LIMIT_REACHED) {
      return rejectWithValue({ type: WATCH_LIMIT_REACHED });
    }

    const { nextEntries } = result;

    // Optimistic update so hearts flip immediately.
    const optimisticUser = {
      ...currentUser,
      attributes: {
        ...currentUser.attributes,
        profile: {
          ...currentUser.attributes.profile,
          privateData: {
            ...currentUser.attributes.profile?.privateData,
            [WATCHED_LISTING_IDS_KEY]: nextEntries,
          },
        },
      },
    };
    dispatch(setCurrentUser(optimisticUser));

    const privateData = omitReservedManagedAuthPrivateData({
      [WATCHED_LISTING_IDS_KEY]: nextEntries,
    });

    return sdk.currentUser
      .updateProfile({ privateData }, { expand: true })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(setCurrentUser(entities[0]));
        return { listingId: listingIdStr, watchedListingIds: nextEntries };
      })
      .catch(e => {
        // Revert optimistic update on failure.
        dispatch(setCurrentUser(currentUser));
        return rejectWithValue(storableError(e));
      });
  },
  {
    // Belt-and-suspenders with WatchButton's click guard: never run concurrent toggles.
    condition: (_, { getState }) => !getState().watchedListings?.toggleInProgress,
  }
);

/**
 * @param {Object} listing
 */
export const toggleWatchedListing = listing => dispatch => {
  return dispatch(toggleWatchedListingThunk(listing)).unwrap();
};

// ================ Slice ================ //

const watchedListingsSlice = createSlice({
  name: 'watchedListings',
  initialState: {
    toggleInProgress: false,
    toggleError: null,
    togglingListingId: null,
  },
  reducers: {
    clearToggleError: state => {
      state.toggleError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(toggleWatchedListingThunk.pending, (state, action) => {
        state.toggleInProgress = true;
        state.toggleError = null;
        state.togglingListingId = toListingIdString(action.meta.arg?.id);
      })
      .addCase(toggleWatchedListingThunk.fulfilled, state => {
        state.toggleInProgress = false;
        state.togglingListingId = null;
      })
      .addCase(toggleWatchedListingThunk.rejected, (state, action) => {
        state.toggleInProgress = false;
        state.togglingListingId = null;
        state.toggleError = action.payload;
      });
  },
});

export const { clearToggleError } = watchedListingsSlice.actions;

export default watchedListingsSlice.reducer;
